import { useQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';

import { IneligibleOpeningsBanner } from './ineligible-openings-banner';

export function SelectedOpenings() {
  const { selectedOpeningIds, setSelectedOpeningIds, ineligibleOpenings } =
    useJobPostingContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(selectedOpeningIds.length);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.jobPosting.channels],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannels(
        selectedOpeningIds,
      ),
  });

  const openingNamesMap =
    useMemo(
      () =>
        data?.selected_openings?.reduce(
          (acc, opening) => ({
            ...acc,
            [opening.external_id]: opening.title,
          }),
          {} as Record<string, string>,
        ),
      [data],
    ) ?? {};

  useLayoutEffect(() => {
    if (isExpanded || !containerRef.current) return;

    const pills =
      containerRef.current.querySelectorAll<HTMLElement>('[data-pill]');

    // Reveal all pills so offsetTop is accurate
    pills.forEach(p => p.removeAttribute('hidden'));

    // Count rows by unique offsetTop values; stop at row 3
    const rows = new Set<number>();
    let count = pills.length;
    for (let i = 0; i < pills.length; i += 1) {
      rows.add(pills[i].offsetTop);
      if (rows.size > 2) {
        count = i;
        break;
      }
    }

    // Hide everything past the 2-row boundary
    pills.forEach((p, i) => p.toggleAttribute('hidden', i >= count));
    setVisibleCount(count);
    setNeedsTruncation(count < pills.length);
  }, [selectedOpeningIds, isExpanded, data]);

  const onRemove = (idToRemove: string) => {
    setSelectedOpeningIds(prevIds => prevIds.filter(id => id !== idToRemove));
  };

  if (isLoading) {
    return <SelectedOpenings.Skeleton />;
  }

  if (selectedOpeningIds.length <= 1 && ineligibleOpenings.length === 0)
    return null;

  return (
    <div className="flex w-full max-w-[750px] flex-col gap-6">
      <Typography variant="caption">
        <FormattedMessage
          defaultMessage="Selected Openings ({count})"
          values={{ count: selectedOpeningIds.length }}
          description="Section title: {count} is how many jobs will receive the post"
        />
      </Typography>

      <div
        ref={containerRef}
        className="flex w-full flex-wrap items-center gap-2"
      >
        {/* Opening pills */}
        {selectedOpeningIds.map((openingId, index) => {
          return (
            <div
              data-pill
              key={openingId}
              hidden={!isExpanded && index >= visibleCount}
              className="flex items-center justify-center gap-2 rounded-[40px] bg-[#E2E8F0] px-3 py-1"
            >
              <Typography
                variant="subtitle1"
                className="text-fx-neutral-900 whitespace-nowrap"
              >
                {openingNamesMap[openingId]}
              </Typography>
              <Button
                variant="ghost"
                className="h-auto !p-0 hover:bg-transparent"
                onClick={() => onRemove(openingId)}
              >
                <CloseIcon className="text-fx-neutral-600" />
              </Button>
            </div>
          );
        })}

        {/* View more / view less button */}
        {needsTruncation && (
          <Button
            variant="link"
            className="h-fit hover:no-underline"
            onClick={() => setIsExpanded(prev => !prev)}
          >
            {isExpanded ? (
              <FormattedMessage
                defaultMessage="View Less"
                description="Show fewer selected openings in the chip list"
              />
            ) : (
              <FormattedMessage
                defaultMessage="View More"
                description="Show full list of selected openings in the post dialog"
              />
            )}
          </Button>
        )}
      </div>
      <IneligibleOpeningsBanner ineligibleOpenings={ineligibleOpenings} />
    </div>
  );
}

SelectedOpenings.Skeleton = function SelectedOpeningsSkeleton() {
  return (
    <div className="flex w-full max-w-[750px] flex-col gap-2">
      <Skeleton className="h-4 w-32" />

      <div className="flex w-full flex-wrap content-start items-start gap-2">
        {_range(8).map(i => (
          <Skeleton
            key={i}
            className="h-7 !rounded-[40px]"
            style={{ width: i % 3 === 0 ? '200px' : '150px' }}
          />
        ))}
      </div>
    </div>
  );
};
